/* import __COLOCATED_TEMPLATE__ from './api-docs-side-drawer.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import type TaskSuggestion from 'embercom/models/content-service/task-suggestion';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'embercom/services/intl';
import { inject as service } from '@ember/service';
import type NotificationsService from 'embercom/services/notifications-service';
// @ts-expect-error
import { sanitizeHtml } from '@intercom/pulse/lib/sanitize';

interface Signature {
  Args: {
    suggestion: TaskSuggestion;
    isOpen: boolean;
    onClose: () => void;
  };
}

export default class ApiDocsSideDrawer extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare notificationsService: InstanceType<typeof NotificationsService>;
  @tracked openSectionId: string | null = null;
  @tracked isHoveringSampleRequest = false;
  @tracked isHoveringCopyButton = false;

  @action
  closeSideDrawer() {
    this.args.onClose();
  }

  @action
  onOpenSectionChange(sectionId: string) {
    this.openSectionId = sectionId;
  }

  get formattedOverview() {
    let text = this.intl.t(
      'automation.analyze.task-suggestions.api-docs-side-drawer.overview.engineer_review',
    );
    return sanitizeHtml(text);
  }

  get formattedApiDocumentationDescription() {
    let text = this.intl.t(
      'automation.analyze.task-suggestions.api-docs-side-drawer.api_documentation.description',
    );
    return sanitizeHtml(text);
  }

  get formattedActionDescription() {
    if (!this.args.suggestion.actionDescription) {
      return '';
    }
    return sanitizeHtml(this.args.suggestion.actionDescription);
  }

  get formattedActionParams() {
    if (!this.hasActionParams) {
      return '{}';
    }
    return JSON.stringify(this.args.suggestion.actionParams, null, 2);
  }

  get hasActionParams() {
    return Object.keys(this.args.suggestion.actionParams).length > 0;
  }

  get hasMockResponse() {
    return Object.keys(this.args.suggestion.mockResponse).length > 0;
  }

  get formattedMockResponse() {
    if (!this.hasMockResponse) {
      return '{}';
    }
    return JSON.stringify(this.args.suggestion.mockResponse, null, 2);
  }

  get whatItDoesActionDescription() {
    let formatted = this.formattedActionDescription;
    if (!formatted) {
      return '';
    }
    let description = this.args.suggestion.actionDescription;
    // Look for "What it does:" followed by content until we hit a delimiter:
    // - HTML closing tag (</p>)
    // - Triple dash separator (---)
    // - Another section heading (When to use:, How it works:, etc.)
    // - End of string
    let whatItDoesMatch = description.match(
      /What it does:\s*(.+?)(?=\s*(?:<\/p>|---|When to use:|$))/is,
    );
    return whatItDoesMatch ? whatItDoesMatch[1].trim() : '';
  }

  get whenToUseActionDescription() {
    let formatted = this.formattedActionDescription;
    if (!formatted) {
      return '';
    }
    let description = this.args.suggestion.actionDescription;
    // Look for "When to use:" followed by content until we hit a delimiter:
    // - HTML closing tag (</p>)
    // - Triple dash separator (---)
    // - Another section heading (What it does:, How it works:, etc.)
    // - End of string
    let whenToUseMatch = description.match(
      /When to use:\s*(.+?)(?=\s*(?:<\/p>|---|What it does:|$))/is,
    );
    return whenToUseMatch ? whenToUseMatch[1].trim() : '';
  }

  // Not used for now until we improve the prompt
  get technicalImplementationComplexity() {
    if (!this.args.suggestion.implementationDifficultyReasoning) {
      return '';
    }

    let description = this.args.suggestion.implementationDifficultyReasoning;

    let firstSectionMatch = description.match(
      /<p><strong>Technical Implementation Complexity:<\/strong><br>\s*(.+?)<\/p>/s,
    );

    if (!firstSectionMatch) {
      return '';
    }

    return firstSectionMatch[1]
      .replace(/<em>/g, '')
      .replace(/<\/em>/g, '')
      .replace(/&amp;/g, '&')
      .trim();
  }

  @action
  async onCopyActionParams() {
    await navigator.clipboard.writeText(this.formattedActionParams);
    this.notificationsService.notifyConfirmation(
      this.intl.t('automation.analyze.task-suggestions.api-docs-side-drawer.data_inputs.copied'),
    );
  }

  @action
  onHoverSampleRequest() {
    this.isHoveringSampleRequest = true;
  }

  @action
  onLeaveSampleRequest() {
    this.isHoveringSampleRequest = false;
  }

  @action
  onCopyButtonHover() {
    this.isHoveringCopyButton = true;
  }

  @action
  onCopyButtonLeave() {
    this.isHoveringCopyButton = false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::FinAiAgent::Analyze::TaskSuggestions::ApiDocsSideDrawer': typeof ApiDocsSideDrawer;
    'reporting/automation/fin-ai-agent/analyze/task-suggestions/api-docs-side-drawer': typeof ApiDocsSideDrawer;
  }
}
